<script setup>
import { computed, ref, nextTick, inject, watch } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';

import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import ChannelIcon from 'dashboard/components-next/icon/ChannelIcon.vue';
import BulkSelectBar from 'dashboard/components-next/pilot/assistant/BulkSelectBar.vue';
import DeleteDialog from 'dashboard/components-next/pilot/pageComponents/DeleteDialog.vue';
import ConnectInboxDialog from 'dashboard/components-next/pilot/pageComponents/inbox/ConnectInboxDialog.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

// 从父组件注入 assistantId
const injectedAssistantId = inject('assistantId');
const assistantId = computed(
  () => injectedAssistantId?.value || props.assistant?.id
);

// Store getters
const uiFlags = useMapGetter('pilotInboxes/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingList);
const pilotInboxes = useMapGetter('pilotInboxes/getRecords');

// 确保 pilotInboxes 是数组
const pilotInboxesArray = computed(() => pilotInboxes.value || []);
const isEmpty = computed(() => !pilotInboxesArray.value.length);

// 搜索
const searchQuery = ref('');

const filteredInboxes = computed(() => {
  const query = searchQuery.value.trim();
  const source = pilotInboxesArray.value;
  if (!query) return source;
  return picoSearch(source, query, ['name']);
});

// 弹窗相关
const dialogType = ref('');
const selectedInbox = ref(null);
const disconnectInboxDialog = ref(null);
const connectInboxDialog = ref(null);

// 批量选择状态
const selectedInboxIds = ref(new Set());
const isBatchRemoving = ref(false);
const hoveredInboxId = ref(null);

// 空状态假数据
const emptyStateInboxes = [
  { id: 1, name: '网站客服', channel_type: 'Channel::WebWidget' },
  { id: 2, name: '微信公众号', channel_type: 'Channel::WeChat' },
  {
    id: 3,
    name: '客服邮箱',
    channel_type: 'Channel::Email',
    email: 'support@example.com',
  },
  { id: 4, name: 'WhatsApp', channel_type: 'Channel::Whatsapp' },
  { id: 5, name: 'Facebook Messenger', channel_type: 'Channel::FacebookPage' },
];

// 切换选中状态
const toggleSelection = inboxId => {
  if (selectedInboxIds.value.has(inboxId)) {
    selectedInboxIds.value.delete(inboxId);
  } else {
    selectedInboxIds.value.add(inboxId);
  }
  selectedInboxIds.value = new Set(selectedInboxIds.value);
};

// 判断是否选中
const isSelected = inboxId => selectedInboxIds.value.has(inboxId);

// 选中数量
const hasSelection = computed(() => selectedInboxIds.value.size > 0);

// 全选/取消全选标签
const buildSelectedCountLabel = computed(() => {
  const count = pilotInboxesArray.value.length || 0;
  const isAllSelected = selectedInboxIds.value.size === count && count > 0;
  return isAllSelected ? `取消全选 (${count})` : `全选 (${count})`;
});

const selectedCountLabel = computed(() => {
  return `已选择 ${selectedInboxIds.value.size} 项`;
});

// 打开连接收件箱弹窗
const handleCreate = () => {
  dialogType.value = 'create';
  nextTick(() => connectInboxDialog.value.open());
};

// 处理收件箱操作
const handleAction = ({ action, id }) => {
  selectedInbox.value = pilotInboxesArray.value.find(inbox => id === inbox.id);
  nextTick(() => {
    if (action === 'delete') {
      disconnectInboxDialog.value.dialogRef.open();
    }
  });
};

// 单个移除
const removingId = ref(null);
const handleRemoveInbox = async inbox => {
  removingId.value = inbox.id;
  try {
    await store.dispatch('pilotInboxes/delete', {
      assistantId: assistantId.value,
      inboxId: inbox.id,
    });
    await fetchData();
    useAlert('收件箱已断开连接');
  } finally {
    removingId.value = null;
  }
};

// 批量移除
const handleBatchRemove = async () => {
  if (!hasSelection.value || isBatchRemoving.value) return;

  isBatchRemoving.value = true;
  try {
    const ids = Array.from(selectedInboxIds.value);
    await Promise.all(
      ids.map(inboxId =>
        store.dispatch('pilotInboxes/delete', {
          assistantId: assistantId.value,
          inboxId: inboxId,
        })
      )
    );
    await fetchData();
    selectedInboxIds.value = new Set();
    useAlert('收件箱已批量断开连接');
  } finally {
    isBatchRemoving.value = false;
  }
};

// 关闭弹窗
const handleCreateClose = () => {
  dialogType.value = '';
  selectedInbox.value = null;
};

// 加载数据
const fetchData = async () => {
  if (!assistantId.value) return;
  try {
    await store.dispatch('pilotInboxes/get', {
      assistantId: assistantId.value,
    });
  } catch (error) {
    // 忽略错误
  }
};

// 监听 assistantId 变化
watch(
  assistantId,
  newId => {
    if (newId) {
      fetchData();
    }
  },
  { immediate: true }
);

// 获取收件箱类型标签
const getChannelTypeLabel = inbox => {
  const typeMap = {
    'Channel::WebWidget': '网站',
    'Channel::WeChat': '微信',
    'Channel::Email': '邮箱',
    'Channel::Whatsapp': 'WhatsApp',
    'Channel::FacebookPage': 'Facebook',
    'Channel::TwitterProfile': 'Twitter',
    'Channel::Telegram': 'Telegram',
    'Channel::Line': 'Line',
    'Channel::Sms': '短信',
    'Channel::Api': 'API',
  };
  return (
    typeMap[inbox.channel_type] ||
    inbox.channel_type?.replace('Channel::', '') ||
    '未知'
  );
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- 一级标题 -->
    <h2 class="text-base font-semibold text-n-slate-12 pt-4">
      {{ t('PILOT.ASSISTANTS.CONFIG.NAV.INBOXES') }}
    </h2>

    <!-- 收件箱配置 -->
    <section class="bg-[#F3F4F7] dark:bg-n-slate-7/50 rounded-xl p-3">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center gap-2">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('PILOT.ASSISTANTS.CONFIG.INBOXES.TITLE') }}
          </h3>
          <span
            v-tooltip="'选择助手生效的收件箱'"
            class="i-lucide-help-circle text-sm text-n-slate-9 cursor-help"
          />
        </div>
      </div>

      <!-- 空状态引导 -->
      <div v-if="isEmpty" class="relative overflow-hidden rounded-xl">
        <!-- 背景假卡片 -->
        <div class="space-y-2 p-4 opacity-50 pointer-events-none">
          <div
            v-for="inbox in emptyStateInboxes"
            :key="inbox.id"
            class="flex items-center justify-between p-3 rounded-lg bg-white dark:bg-n-slate-800 border border-n-slate-3 dark:border-n-slate-700"
          >
            <div class="flex items-center gap-2">
              <ChannelIcon :inbox="inbox" />
              <span class="text-sm text-n-slate-12">{{ inbox.name }}</span>
            </div>
          </div>
        </div>

        <!-- 渐变蒙层 + 内容 -->
        <div
          class="absolute inset-0 flex flex-col items-center justify-end pb-10 bg-gradient-to-t from-[#F3F4F7] from-30% to-transparent dark:from-n-slate-7/50 dark:to-transparent"
        >
          <h3 class="text-xl font-medium text-n-slate-12 mb-2">
            关联收件箱，启用智能客服
          </h3>
          <p class="text-sm text-n-slate-11 mb-4 max-w-md text-center">
            将助手连接到收件箱后，它将自动处理来自网站、微信、邮件等渠道的用户咨询
          </p>
          <button
            class="px-4 py-2 bg-n-brand text-white text-sm rounded-lg hover:bg-n-brand-dark transition-colors"
            @click="handleCreate"
          >
            连接收件箱
          </button>
        </div>
      </div>

      <!-- 已关联的收件箱列表 -->
      <div v-else class="flex flex-col gap-2">
        <!-- 批量操作工具条 -->
        <BulkSelectBar
          v-model="selectedInboxIds"
          :all-items="pilotInboxesArray"
          :select-all-label="buildSelectedCountLabel"
          :selected-count-label="selectedCountLabel"
          delete-label="批量断开"
          no-background
          @bulk-delete="handleBatchRemove"
        >
          <template #default-actions>
            <div class="flex justify-between items-center w-full">
              <Input v-model="searchQuery" size="sm" placeholder="搜索收件箱" />
              <Button label="连接更多" sm slate @click="handleCreate" />
            </div>
          </template>
        </BulkSelectBar>

        <!-- 搜索无结果 -->
        <div v-if="filteredInboxes.length === 0" class="py-4 text-center">
          <span class="text-sm text-n-slate-11">没有找到匹配的收件箱</span>
        </div>

        <!-- 收件箱列表 -->
        <div v-else class="flex flex-col gap-2">
          <div
            v-for="inbox in filteredInboxes"
            :key="inbox.id"
            class="relative flex items-center justify-between w-full gap-2 p-4 rounded-xl outline-1 outline transition-colors"
            :class="[
              isSelected(inbox.id)
                ? 'outline-n-brand bg-n-brand-50 dark:bg-n-brand-500/10'
                : 'outline-n-container bg-n-solid-2',
            ]"
            @mouseenter="hoveredInboxId = inbox.id"
            @mouseleave="hoveredInboxId = null"
          >
            <!-- Checkbox 绝对定位在左上角，hover 或选中时显示 -->
            <div
              v-show="hoveredInboxId === inbox.id || selectedInboxIds.size > 0"
              class="absolute top-4 ltr:left-3 rtl:right-3"
            >
              <Checkbox
                :model-value="isSelected(inbox.id)"
                @update:model-value="toggleSelection(inbox.id)"
              />
            </div>

            <!-- 左侧内容 -->
            <div
              class="flex items-center gap-3 flex-1 min-w-0 ltr:ml-6 rtl:mr-6"
            >
              <!-- 图标/头像 -->
              <div
                class="w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden bg-n-slate-3 dark:bg-n-slate-700"
              >
                <img
                  v-if="inbox.avatar_url"
                  :src="inbox.avatar_url"
                  :alt="inbox.name"
                  class="w-full h-full object-cover"
                />
                <ChannelIcon v-else :inbox="inbox" class="size-5" />
              </div>
              <!-- 名称和类型 -->
              <div class="flex flex-col min-w-0">
                <span class="text-sm text-n-slate-12 font-medium truncate">{{
                  inbox.name
                }}</span>
                <span class="text-sm text-n-slate-11 mt-2">{{
                  getChannelTypeLabel(inbox)
                }}</span>
              </div>
            </div>
            <!-- 删除按钮 -->
            <Button
              icon="i-lucide-trash"
              slate
              xs
              ghost
              :is-loading="removingId === inbox.id"
              :disabled="removingId !== null"
              @click="handleRemoveInbox(inbox)"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- 断开连接确认弹窗 -->
    <DeleteDialog
      v-if="selectedInbox"
      ref="disconnectInboxDialog"
      :entity="selectedInbox"
      :delete-payload="{
        assistantId: assistantId,
        inboxId: selectedInbox.id,
      }"
      type="Inboxes"
      translation-key="INBOXES"
      @delete-success="fetchData"
    />

    <!-- 连接收件箱弹窗 -->
    <ConnectInboxDialog
      v-if="dialogType"
      ref="connectInboxDialog"
      :assistant-id="assistantId"
      :type="dialogType"
      @close="handleCreateClose"
    />
  </div>
</template>
